import { AudioFileBox, AudioPitchStretchBox, AudioSignalsmithBox, AudioTimeStretchBox, WarpMarkerBox } from "@opendaw/studio-boxes";
import { PPQN, TimeBase } from "@opendaw/lib-dsp";
import { TransientPlayMode } from "@opendaw/studio-enums";
import { asInstanceOf, isDefined, isInstanceOf, panic, quantizeRound, UUID } from "@opendaw/lib-std";
import { Guard } from "../Guard";
import { AudioFiles } from "../AudioFiles";
export var AudioContents;
(function (AudioContents) {
    AudioContents.Playbacks = ["no-sync", "pitch", "timestretch", "signalsmith"];
    AudioContents.playModeBox = (box) => box.playMode.targetVertex.mapOr(vertex => vertex.box, null);
    AudioContents.playback = (box) => {
        if (box.timeBase.getValue() === TimeBase.Seconds) {
            return "no-sync";
        }
        const playMode = AudioContents.playModeBox(box);
        if (playMode instanceof AudioPitchStretchBox) {
            return "pitch";
        }
        if (playMode instanceof AudioTimeStretchBox) {
            return "timestretch";
        }
        if (playMode instanceof AudioSignalsmithBox) {
            return "signalsmith";
        }
        return "no-sync";
    };
    AudioContents.sample = (context, box) => AudioFiles.toSample(context, asInstanceOf(box.file.targetVertex.unwrap("audio content has no file").box, AudioFileBox));
    AudioContents.defaultPlayback = (sample) => sample.bpm > 0 ? "pitch" : "no-sync";
    AudioContents.defaultDuration = (sample, playback, projectBpm) => {
        if (playback === "no-sync") {
            return sample.duration;
        }
        const pulses = PPQN.secondsToPulses(sample.duration, sample.bpm > 0 ? sample.bpm : projectBpm);
        return pulses < PPQN.SemiQuaver ? pulses : quantizeRound(pulses, PPQN.SemiQuaver);
    };
    AudioContents.validatePlayback = (value) => Guard.oneOf(value, AudioContents.Playbacks, "playback");
    AudioContents.createPlayMode = (context, playback, durationInPPQN, durationInSeconds, props) => {
        const boxGraph = context.boxGraph;
        if (playback === "no-sync") {
            return null;
        }
        const playMode = playback === "pitch"
            ? AudioPitchStretchBox.create(boxGraph, UUID.generate())
            : playback === "timestretch"
                ? AudioTimeStretchBox.create(boxGraph, UUID.generate(), box => {
                    box.transientPlayMode.setValue(props.transientPlayMode ?? TransientPlayMode.Pingpong);
                    box.playbackRate.setValue(props.playbackRate ?? 1.0);
                })
                : AudioSignalsmithBox.create(boxGraph, UUID.generate(), box => {
                    box.transpose.setValue(props.transpose ?? 0.0);
                });
        if (durationInPPQN <= 0 || durationInSeconds <= 0) {
            return panic(new RangeError(`Audio content requires positive durations (ppqn: ${durationInPPQN}, seconds: ${durationInSeconds})`));
        }
        WarpMarkerBox.create(boxGraph, UUID.generate(), box => {
            box.owner.refer(playMode.warpMarkers);
            box.position.setValue(0);
            box.seconds.setValue(0.0);
        });
        WarpMarkerBox.create(boxGraph, UUID.generate(), box => {
            box.owner.refer(playMode.warpMarkers);
            box.position.setValue(durationInPPQN);
            box.seconds.setValue(durationInSeconds);
        });
        return playMode;
    };
    AudioContents.transientPlayMode = (box) => {
        const playMode = AudioContents.playModeBox(box);
        return playMode instanceof AudioTimeStretchBox ? playMode.transientPlayMode.getValue() : TransientPlayMode.Pingpong;
    };
    AudioContents.setTransientPlayMode = (context, box, value) => {
        const playMode = requirePlayMode(box, AudioTimeStretchBox, "transientPlayMode", "timestretch");
        const validated = Guard.oneOf(value, [TransientPlayMode.Once, TransientPlayMode.Repeat, TransientPlayMode.Pingpong], "transientPlayMode");
        context.edit(() => playMode.transientPlayMode.setValue(validated));
    };
    AudioContents.playbackRate = (box) => {
        const playMode = AudioContents.playModeBox(box);
        return playMode instanceof AudioTimeStretchBox ? playMode.playbackRate.getValue() : 1.0;
    };
    AudioContents.setPlaybackRate = (context, box, value) => {
        const playMode = requirePlayMode(box, AudioTimeStretchBox, "playbackRate", "timestretch");
        const validated = Guard.float32("positive", value, "playbackRate");
        context.edit(() => playMode.playbackRate.setValue(validated));
    };
    AudioContents.transpose = (box) => {
        const playMode = AudioContents.playModeBox(box);
        return playMode instanceof AudioSignalsmithBox ? playMode.transpose.getValue() : 0.0;
    };
    AudioContents.setTranspose = (context, box, value) => {
        const playMode = requirePlayMode(box, AudioSignalsmithBox, "transpose", "signalsmith");
        const validated = Guard.float32({ min: -24.0, max: 24.0, scaling: "linear" }, value, "transpose");
        context.edit(() => playMode.transpose.setValue(validated));
    };
    const requirePlayMode = (box, type, name, playback) => {
        const playMode = AudioContents.playModeBox(box);
        if (isDefined(playMode) && isInstanceOf(playMode, type)) {
            return playMode;
        }
        return panic(new RangeError(`${name} is only available for playback "${playback}", this content plays "${AudioContents.playback(box)}"`));
    };
})(AudioContents || (AudioContents = {}));
